/**
 * FrameTheory — Theme JavaScript
 * Core interactions: dark mode, mobile menu, smooth scroll, animations, footer columns.
 */

document.addEventListener('DOMContentLoaded', () => {
  initTheme();
  initAccentContrast();
  initMobileMenu();
  initNavigationDropdowns();
  initFooterColumns();
  initScrollToTop();
  initReadingProgress();
  initCopyLink();
  initArchiveView();
  initContactForm();
  initHeroSlider();
});

/**
 * Accent contrast — derives a readable text color for accent-filled elements
 * from whatever accent the admin picks (Brand → Accent color → --ghost-accent-color).
 * Sets --accent-contrast to dark or white based on the accent's relative luminance,
 * so a light/pastel accent doesn't leave white-on-light text unreadable.
 */
function initAccentContrast() {
  const root = document.documentElement;
  const raw = getComputedStyle(root).getPropertyValue('--ghost-accent-color').trim();
  const rgb = parseAccentColor(raw);
  if (!rgb) return; // No accent set — CSS fallback (#fff) stands.
  const toLinear = (c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * toLinear(rgb[0]) + 0.7152 * toLinear(rgb[1]) + 0.0722 * toLinear(rgb[2]);
  const isAccentDark = luminance <= 0.18;
  root.style.setProperty('--accent-contrast', luminance > 0.5 ? '#111111' : '#ffffff');
  root.style.setProperty('--accent-readable-dark', isAccentDark ? '#f5f5f5' : 'var(--ghost-accent-color)');
  root.style.setProperty('--accent-readable-dark-contrast', isAccentDark ? '#111111' : 'var(--accent-contrast)');
}

/**
 * Parse a CSS color (hex #rgb/#rrggbb or rgb()/rgba()) into [r,g,b]. Returns null if unparseable.
 */
function parseAccentColor(str) {
  if (!str) return null;
  const hex = str.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const fn = str.match(/rgba?\(([^)]+)\)/i);
  if (fn) {
    const parts = fn[1].split(/[ ,/]+/).map(parseFloat).filter(n => !isNaN(n));
    if (parts.length >= 3) return [parts[0], parts[1], parts[2]];
  }
  return null;
}

/**
 * Theme Management (Light / Dark Mode) — localStorage key: frametheory-theme
 * The no-flash <head> script sets the initial state; this binds the toggle.
 */
function initTheme() {
  const themeToggleButtons = document.querySelectorAll('[data-theme-toggle]');

  const apply = (dark, writeToStorage = false) => {
    document.documentElement.classList.toggle('dark', dark);
    document.body.classList.toggle('dark', dark);
    if (writeToStorage) {
      localStorage.setItem('frametheory-theme', dark ? 'dark' : 'light');
    }
    themeToggleButtons.forEach(btn =>
      btn.setAttribute('aria-label', dark ? 'Switch to light mode' : 'Switch to dark mode'));
  };

  // Sync <body> with the class the no-flash script already set on <html>
  // Do NOT write to localStorage on initial load
  apply(document.documentElement.classList.contains('dark'), false);

  themeToggleButtons.forEach(button => {
    button.addEventListener('click', () => {
      const isDarkNow = document.documentElement.classList.contains('dark');
      apply(!isDarkNow, true);
    });
  });
}

/**
 * Mobile Navigation Menu (with focus trap)
 */
function initMobileMenu() {
  const menuTrigger = document.querySelector('[data-menu-trigger]');
  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const menuClose = document.querySelector('[data-menu-close]');
  if (!menuTrigger || !mobileMenu) return;

  const backdrop = mobileMenu.querySelector('[data-menu-backdrop]');
  const drawer = mobileMenu.querySelector('[data-menu-drawer]');
  let isTransitioning = false;

  const openMenu = () => {
    if (isTransitioning) return;
    isTransitioning = true;
    mobileMenu.classList.remove('hidden');
    menuTrigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    void mobileMenu.offsetWidth;
    if (backdrop) {
      backdrop.classList.remove('opacity-0', 'pointer-events-none');
      backdrop.classList.add('opacity-100', 'pointer-events-auto');
    }
    if (drawer) {
      drawer.classList.remove('translate-x-full');
      drawer.classList.add('translate-x-0');
    }
    const navLinks = mobileMenu.querySelectorAll('[data-mobile-menu-link]');
    const footerCta = mobileMenu.querySelector('[data-menu-cta]');
    navLinks.forEach((link, i) => {
      link.classList.add('menu-link-reveal');
      link.style.transitionDelay = (150 + i * 60) + 'ms';
    });
    if (footerCta) {
      footerCta.classList.add('menu-link-reveal');
      footerCta.style.transitionDelay = '400ms';
    }
    requestAnimationFrame(() => {
      navLinks.forEach(link => link.classList.add('is-revealed'));
      if (footerCta) footerCta.classList.add('is-revealed');
    });
    const focusable = mobileMenu.querySelectorAll('button, [href], input, select, textarea');
    if (focusable.length > 0) setTimeout(() => focusable[0].focus(), 150);
    mobileMenu.addEventListener('keydown', trapFocus);
    setTimeout(() => { isTransitioning = false; }, 300);
  };

  const closeMenu = () => {
    if (isTransitioning) return;
    isTransitioning = true;
    if (backdrop) {
      backdrop.classList.remove('opacity-100', 'pointer-events-auto');
      backdrop.classList.add('opacity-0', 'pointer-events-none');
    }
    if (drawer) {
      drawer.classList.remove('translate-x-0');
      drawer.classList.add('translate-x-full');
    }
    menuTrigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    mobileMenu.removeEventListener('keydown', trapFocus);
    setTimeout(() => {
      mobileMenu.classList.add('hidden');
      menuTrigger.focus();
      isTransitioning = false;
      mobileMenu.querySelectorAll('.menu-link-reveal.is-revealed').forEach(el => el.classList.remove('is-revealed'));
    }, 300);
  };

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusable = mobileMenu.querySelectorAll('button, [href], input, select, textarea');
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { last.focus(); e.preventDefault(); }
    } else {
      if (document.activeElement === last) { first.focus(); e.preventDefault(); }
    }
  }

  menuTrigger.addEventListener('click', openMenu);
  if (menuClose) menuClose.addEventListener('click', closeMenu);
  if (backdrop) backdrop.addEventListener('click', closeMenu);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !mobileMenu.classList.contains('hidden')) closeMenu();
  });
}

/**
 * Footer multi-column navigation builder.
 * Distributes secondary-nav links into columns by "##"/heading markers.
 */
function initFooterColumns() {
  const data = document.getElementById('frametheory-footer-nav-data');
  if (!data || !data.content) return;
  const items = data.content.querySelectorAll('[data-nav-item]');
  if (!items.length) return;

  const cols = document.querySelectorAll('[data-footer-col]');
  if (!cols.length) return;
  let colIdx = -1;
  let currentList = null;
  let usedHeadings = false;

  items.forEach((item) => {
    const slug = item.getAttribute('data-slug') || '';
    const url = item.getAttribute('data-url') || '';
    const label = item.getAttribute('data-label') || '';
    const isHeading = slug.indexOf('heading') !== -1 || slug.indexOf('hash-hash') !== -1 || url.indexOf('##') !== -1;

    if (isHeading && colIdx < cols.length - 1) {
      usedHeadings = true;
      colIdx++;
      const heading = cols[colIdx].querySelector('[data-col-heading]');
      currentList = cols[colIdx].querySelector('[data-col-links]');
      if (heading) heading.textContent = label;
    } else {
      // No heading yet — start filling the first column
      if (!currentList) {
        colIdx = 0;
        currentList = cols[0].querySelector('[data-col-links]');
      }
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = url;
      a.className = 'text-[11px] font-display font-semibold text-white hover:text-muted-dark uppercase tracking-wider transition-colors';
      a.textContent = label;
      li.appendChild(a);
      if (currentList) currentList.appendChild(li);
    }
  });

  // Hide columns that ended up empty
  cols.forEach((col) => {
    const list = col.querySelector('[data-col-links]');
    if (list && list.children.length === 0) col.style.display = 'none';
  });
}

/**
 * Scroll to Top
 */
function initScrollToTop() {
  document.querySelectorAll('[data-scroll-top]').forEach(btn => {
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  });
}

/**
 * Archive grid/list view toggle (tag / author pages)
 */
function initArchiveView() {
  const container = document.querySelector('[data-posts-container]');
  const buttons = document.querySelectorAll('[data-view]');
  if (!container || !buttons.length) return;

  const activeOn = ['bg-black', 'text-bg-light', 'dark:bg-white', 'dark:text-black', 'border-black', 'dark:border-white'];
  const activeOff = ['bg-transparent', 'text-text-light', 'dark:text-text-dark', 'border-[#111111]/15', 'dark:border-white/10'];

  const applyLayoutToCard = (card, layout) => {
    const article = card.querySelector('article');
    const imgContainer = card.querySelector('.img-container');
    const contentContainer = card.querySelector('.content-container');
    const shadow = card.querySelector('.absolute.-z-10') || card.querySelector('div[class*="-z-10"]');

    if (layout === 'list') {
      if (shadow) shadow.classList.add('hidden');
      if (article) {
        article.className = 'relative bg-transparent dark:bg-transparent p-0 border-0 border-b border-[#111111]/10 dark:border-white/10 pb-8 flex flex-col md:flex-row gap-6 md:gap-8 items-start h-auto w-full transition-all duration-300';
      }
      if (imgContainer) {
        imgContainer.className = 'img-container w-full md:w-1/4 aspect-[16/10] bg-gray-300 dark:bg-zinc-700 overflow-hidden relative z-0 shrink-0';
      }
      if (contentContainer) {
        contentContainer.className = 'content-container flex-grow flex flex-col justify-between space-y-3 relative z-20 py-1';
      }
    } else {
      if (shadow) shadow.classList.remove('hidden');
      if (article) {
        article.className = 'relative bg-[#dcd8ce] dark:bg-zinc-800 p-5 border border-black/5 dark:border-white/5 transition-all duration-300 group-hover:bg-white dark:group-hover:bg-zinc-700/80 group-hover:-translate-x-1 group-hover:-translate-y-1 flex flex-col h-full';
      }
      if (imgContainer) {
        imgContainer.className = 'img-container w-full aspect-[16/10] bg-gray-300 dark:bg-zinc-700 overflow-hidden mb-4 relative z-0';
      }
      if (contentContainer) {
        contentContainer.className = 'content-container flex-grow flex flex-col justify-between space-y-4 relative z-20 pointer-events-none';
      }
    }
  };

  const setView = (view) => {
    if (view === 'list') {
      container.className = 'flex flex-col gap-6';
    } else {
      container.className = 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8';
    }

    const activeCards = container.querySelectorAll('.post-card');
    activeCards.forEach(card => applyLayoutToCard(card, view));

    buttons.forEach(btn => {
      const isActive = btn.getAttribute('data-view') === view;
      activeOn.forEach(c => btn.classList.toggle(c, isActive));
      activeOff.forEach(c => btn.classList.toggle(c, !isActive));
    });

    localStorage.setItem('frametheory-archive-layout', view);
  };

  // Load saved preference
  const savedView = localStorage.getItem('frametheory-archive-layout') || 'grid';
  setView(savedView);

  buttons.forEach(btn => {
    btn.addEventListener('click', () => setView(btn.getAttribute('data-view')));
  });
}

/**
 * Contact form — composes a mailto link (no backend invented)
 */
function initContactForm() {
  const form = document.querySelector('[data-contact-form]');
  if (!form) return;
  const success = document.querySelector('[data-contact-success]');
  const reset = document.querySelector('[data-contact-reset]');
  const to = form.getAttribute('data-contact-email');

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const name = (form.querySelector('[name="name"]') || {}).value || '';
    const email = (form.querySelector('[name="email"]') || {}).value || '';
    const subject = (form.querySelector('[name="subject"]') || {}).value || 'Website enquiry';
    const message = (form.querySelector('[name="message"]') || {}).value || '';
    const body = 'Name: ' + name + '\nEmail: ' + email + '\n\n' + message;
    if (to) {
      window.location.href = 'mailto:' + to + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
    }
    if (success) { form.classList.add('hidden'); success.classList.remove('hidden'); }
  });

  if (reset && success) {
    reset.addEventListener('click', () => {
      form.reset();
      success.classList.add('hidden');
      form.classList.remove('hidden');
    });
  }
}

/**
 * Reading progress bar (post page)
 */
function initReadingProgress() {
  const bar = document.querySelector('[data-reading-progress]');
  if (!bar) return;
  const update = () => {
    const winScroll = document.documentElement.scrollTop || document.body.scrollTop;
    const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    bar.style.width = (height > 0 ? (winScroll / height) * 100 : 0) + '%';
  };
  window.addEventListener('scroll', update, { passive: true });
  update();
}

/**
 * Copy-link button (post share panel)
 */
function initCopyLink() {
  document.querySelectorAll('[data-copy-link]').forEach(btn => {
    btn.addEventListener('click', () => {
      const url = btn.getAttribute('data-url') || window.location.href;
      const toast = btn.parentNode.querySelector('[data-copy-toast]');
      navigator.clipboard.writeText(url).then(() => {
        if (!toast) return;
        toast.classList.remove('opacity-0');
        toast.classList.add('opacity-100');
        setTimeout(() => {
          toast.classList.remove('opacity-100');
          toast.classList.add('opacity-0');
        }, 1500);
      }).catch(() => {});
    });
  });
}

/**
 * Navigation Dropdowns / Accordions (Dynamic parsing of hyphen-prefixed navigation links)
 */
function initNavigationDropdowns() {
  // --- 1. Desktop Hover Dropdowns ---
  const headerNav = document.querySelector('[data-header-nav]');
  if (headerNav) {
    const items = headerNav.querySelectorAll('[data-nav-item]');
    let currentParent = null;
    let dropdownList = null;

    items.forEach(item => {
      const link = item.querySelector('a');
      if (!link) return;
      const label = link.textContent.trim();

      if (label.startsWith('-')) {
        // Child menu item
        link.textContent = label.substring(1).trim();
        
        const isChildCurrent = link.classList.contains('is-current') || (link.href === window.location.href);
        if (isChildCurrent && currentParent) {
          const parentLink = currentParent.querySelector('a');
          if (parentLink) {
            parentLink.classList.add('is-current', 'text-text-light', 'dark:text-text-dark');
            parentLink.classList.remove('text-text-light/60', 'dark:text-text-dark/60');
          }
        }
        
        if (currentParent && !dropdownList) {
          currentParent.classList.add('has-dropdown');
          
          // Add small down indicator to parent link
          const arrow = document.createElement('span');
          arrow.className = 'ml-1.5 inline-flex items-center align-middle';
          arrow.innerHTML = `
            <svg class="dropdown-chevron w-2.5 h-2.5 opacity-60 transition-transform duration-250" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          `;
          currentParent.querySelector('a').appendChild(arrow);
          
          // Dropdown absolute container — mt-0 + pt-3 keeps visual gap filled by ::before bridge in CSS
          dropdownList = document.createElement('ul');
          dropdownList.className = 'absolute top-full left-1/2 mt-3 w-44 bg-surface-light dark:bg-surface-dark border border-black/10 dark:border-white/10 pb-2 pt-2 px-2 z-50 shadow-lg';
          currentParent.appendChild(dropdownList);
        }

        if (dropdownList) {
          if (isChildCurrent) {
            link.className = 'block py-2 px-3 text-[11px] uppercase tracking-[0.2em] font-display font-semibold text-text-light dark:text-text-dark bg-black/[0.04] dark:bg-white/[0.04] text-center transition-colors whitespace-nowrap';
          } else {
            link.className = 'block py-2 px-3 text-[11px] uppercase tracking-[0.2em] font-display font-semibold text-text-light/60 dark:text-text-dark/60 hover:text-text-light dark:hover:text-text-dark hover:bg-black/5 dark:hover:bg-white/5 text-center transition-colors whitespace-nowrap';
          }
          dropdownList.appendChild(item);
        }
      } else {
        // Parent menu item
        currentParent = item;
        dropdownList = null;
      }
    });
  }

  // --- 2. Mobile Accordion Dropdowns ---
  const mobileNav = document.querySelector('[data-mobile-nav]');
  if (mobileNav) {
    const items = mobileNav.querySelectorAll('[data-nav-item]');
    let currentParent = null;
    let dropdownContainer = null;
    let parentArrow = null;

    items.forEach(item => {
      const link = item.querySelector('a');
      if (!link) return;
      const labelText = link.querySelector('span:not(.font-mono)') || link;
      const label = labelText.textContent.trim();

      if (label.startsWith('-')) {
        // Child item
        labelText.textContent = label.substring(1).trim();

        if (currentParent && !dropdownContainer) {
          currentParent.classList.add('has-mobile-dropdown');

          const parentLink = currentParent.querySelector('a');
          parentLink.classList.add('flex', 'items-center', 'w-full');

          // Add toggle chevron arrow
          parentArrow = document.createElement('span');
          parentArrow.setAttribute('data-mobile-chevron', '');
          parentArrow.className = 'text-sm font-sans font-normal transition-transform duration-300 pointer-events-none';
          parentArrow.style.marginLeft = 'auto'; // `ml-auto` is purged from the Tailwind build; inline keeps the chevron flush right
          parentArrow.innerHTML = `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>`;
          parentLink.appendChild(parentArrow);

          // Submenu container list
          dropdownContainer = document.createElement('ul');
          dropdownContainer.setAttribute('data-mobile-submenu', '');
          dropdownContainer.className = 'mt-2 border-l border-black/5 dark:border-white/5 space-y-2 hidden overflow-hidden transition-all duration-300';
          dropdownContainer.style.paddingLeft = '2rem'; // `pl-4` purged from the build; inline indent aligns children under the parent label
          currentParent.appendChild(dropdownContainer);

          // Capture per-parent references so each handler toggles its OWN submenu.
          // (Without these, every handler would close over the shared loop variables
          //  and end up toggling only the last parent's submenu.)
          const thisContainer = dropdownContainer;
          const thisArrow = parentArrow;

          // Prevent transition on parent click if URL ends with # or is empty
          parentLink.addEventListener('click', (e) => {
            const href = parentLink.getAttribute('href');
            if (href === '#' || href.endsWith('#') || href.endsWith('/#')) {
              e.preventDefault();
            }

            const isHidden = thisContainer.classList.contains('hidden');

            // Close other mobile menus for clean accordion behavior
            document.querySelectorAll('[data-mobile-submenu]').forEach(el => {
              if (el !== thisContainer) el.classList.add('hidden');
            });
            document.querySelectorAll('[data-mobile-chevron]').forEach(chevron => {
              if (chevron !== thisArrow) chevron.style.transform = 'rotate(0deg)';
            });

            thisContainer.classList.toggle('hidden', !isHidden);
            thisArrow.style.transform = isHidden ? 'rotate(180deg)' : 'rotate(0deg)'; // `rotate-180` purged; inline transform flips the chevron
          });
        }

        if (dropdownContainer) {
          link.className = 'group flex items-baseline font-display font-medium text-lg text-text-light/80 dark:text-text-dark/80 hover:text-text-light dark:hover:text-text-dark transition-colors py-1';
          const monoSpan = link.querySelector('.font-mono');
          if (monoSpan) monoSpan.remove(); // Remove numbered prefix from child elements
          dropdownContainer.appendChild(item);
        }
      } else {
        // Parent menu item
        currentParent = item;
        dropdownContainer = null;
        parentArrow = null;
      }
    });
  }
}

/**
 * Premium Hero Slider carousel logic (fully responsive, swipe tracking, autoplay/pause on hover, scroll adjustments).
 */
function initHeroSlider() {
  const container = document.getElementById('hero-slider-container');
  const prevBtn = document.getElementById('slider-prev');
  const nextBtn = document.getElementById('slider-next');
  const dots = document.querySelectorAll('.slider-dot');
  if (!container || !dots.length) return;

  let currentIndex = 0;
  const totalSlides = dots.length;
  let autoplayTimer = null;

  const updateDots = (index) => {
    dots.forEach((dot, idx) => {
      if (idx === index) {
        dot.className = 'slider-dot w-6 h-2 rounded-full bg-white transition-all duration-300';
        dot.style.opacity = '1';
      } else {
        dot.className = 'slider-dot w-2 h-2 rounded-full bg-white/40 hover:bg-white transition-all duration-300';
        dot.style.opacity = '';
      }
    });
  };

  const scrollToSlide = (index) => {
    if (index < 0) index = totalSlides - 1;
    if (index >= totalSlides) index = 0;
    
    currentIndex = index;
    const slideWidth = container.clientWidth;
    
    // Temporarily disable snap to prevent browser snaps fighting programmatic scrolls
    container.style.scrollSnapType = 'none';
    container.scrollTo({
      left: currentIndex * slideWidth,
      behavior: 'smooth'
    });
    updateDots(currentIndex);
    
    // Re-enable snap after the smooth scroll finishes (approx 600ms)
    setTimeout(() => {
      container.style.scrollSnapType = '';
    }, 600);
  };

  // Click handler for dots
  dots.forEach((dot, idx) => {
    dot.addEventListener('click', () => {
      scrollToSlide(idx);
      resetAutoplay();
    });
  });

  // Click handlers for arrows
  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      scrollToSlide(currentIndex - 1);
      resetAutoplay();
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      scrollToSlide(currentIndex + 1);
      resetAutoplay();
    });
  }

  // Scroll listener to update dots on touch swipe
  let scrollTimeout;
  container.addEventListener('scroll', () => {
    // Only track scroll if user is not mouse dragging
    if (isDown) return;
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      const slideWidth = container.clientWidth;
      const index = Math.round(container.scrollLeft / slideWidth);
      if (index !== currentIndex && index >= 0 && index < totalSlides) {
        currentIndex = index;
        updateDots(currentIndex);
      }
    }, 100);
  });

  // Autoplay function
  const startAutoplay = () => {
    autoplayTimer = setInterval(() => {
      scrollToSlide(currentIndex + 1);
    }, 5000);
  };

  const resetAutoplay = () => {
    clearInterval(autoplayTimer);
    startAutoplay();
  };

  // Pause autoplay on hover
  container.addEventListener('mouseenter', () => clearInterval(autoplayTimer));
  container.addEventListener('mouseleave', startAutoplay);

  // Desktop Mouse Drag to Scroll Logic
  let isDown = false;
  let isDragging = false;
  let startX;
  let scrollLeft;
  const dragThreshold = 5;

  container.addEventListener('mousedown', (e) => {
    isDown = true;
    isDragging = false;
    // Disable smooth scroll temporarily so drag responds 1:1
    container.style.scrollBehavior = 'auto';
    container.style.scrollSnapType = 'none';
    startX = e.pageX - container.offsetLeft;
    scrollLeft = container.scrollLeft;
    clearInterval(autoplayTimer);
  });

  container.addEventListener('mouseleave', () => {
    if (!isDown) return;
    isDown = false;
    container.style.scrollBehavior = 'smooth';
    container.style.scrollSnapType = '';
    snapToNearest();
    startAutoplay();
  });

  container.addEventListener('mouseup', () => {
    if (!isDown) return;
    isDown = false;
    container.style.scrollBehavior = 'smooth';
    container.style.scrollSnapType = '';
    snapToNearest();
    startAutoplay();
  });

  container.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5; // Drag sensitivity multiplier
    if (Math.abs(walk) > dragThreshold) {
      isDragging = true;
    }
    container.scrollLeft = scrollLeft - walk;
  });

  // Prevent link click when dragging
  container.addEventListener('click', (e) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  const snapToNearest = () => {
    const slideWidth = container.clientWidth;
    const index = Math.round(container.scrollLeft / slideWidth);
    scrollToSlide(index);
  };

  // Initial load setup
  updateDots(0);
  startAutoplay();

  // Re-scroll on resize to keep correct slide aligned
  window.addEventListener('resize', () => {
    const slideWidth = container.clientWidth;
    container.scrollLeft = currentIndex * slideWidth;
  });
}